interface MetricReferenceRow {
  metric_outputs?: Record<string, unknown>
  metric_values?: Record<string, unknown> | null
  metric_averages?: Record<string, unknown>
  metric_ids?: string[]
}

/** Include stored source IDs when requesting results for presentation identities. */
export function expandMetricReferenceIds(
  metricIds: string[],
  aliases: Record<string, string>,
): string[] {
  const canonicalIds = new Set(metricIds.map((id) => aliases[id] ?? id))
  const expanded = new Set([...canonicalIds, ...metricIds])
  for (const [source, canonical] of Object.entries(aliases)) {
    if (canonicalIds.has(canonical)) {
      expanded.add(source)
    }
  }
  return [...expanded]
}

/** Resolve presentation identities while retaining their associated values. */
export function resolveMetricReferenceMap<T>(
  values: Record<string, T>,
  aliases: Record<string, string>,
): Record<string, T> {
  const resolved = { ...values }
  for (const [source, canonical] of Object.entries(aliases)) {
    if (!Object.hasOwn(values, source)) {
      continue
    }
    // A persisted canonical result wins even when its value is null or zero.
    if (!Object.hasOwn(values, canonical)) {
      resolved[canonical] = values[source]
    }
    delete resolved[source]
  }
  return resolved
}

/** Resolve presentation keys while retaining the original output and version identities. */
export function resolveMetricReferenceKeys<T extends MetricReferenceRow>(
  row: T,
  aliases: Record<string, string> = {},
): T {
  if (Object.keys(aliases).length === 0) {
    return row
  }
  return {
    ...row,
    ...(row.metric_outputs && {
      metric_outputs: resolveMetricReferenceMap(row.metric_outputs, aliases),
    }),
    ...(row.metric_values && {
      metric_values: resolveMetricReferenceMap(row.metric_values, aliases),
    }),
    ...(row.metric_averages && {
      metric_averages: resolveMetricReferenceMap(row.metric_averages, aliases),
    }),
    ...(row.metric_ids && {
      metric_ids: [...new Set(row.metric_ids.map((id) => aliases[id] ?? id))],
    }),
  }
}
